import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaSave, FaTimes, FaPlus, FaTrash, FaCamera, FaIdBadge, FaStethoscope, FaFlask, FaEdit, FaCheck
} from 'react-icons/fa';
import Nav from './Nav';
import FeedbackModal from './shared/FeedbackModal';
import { useAuth } from '../context/AuthContext';
import userService from '../services/userService';
import CountrySelector from './common/CountrySelector';
import './ProfileEdit.css';

const ProfileEdit = () => {
    const navigate = useNavigate();
    const { user, token, refreshUser } = useAuth();
    const avatarInputRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [removePhoto, setRemovePhoto] = useState(false);
    const [feedback, setFeedback] = useState({ isOpen: false, title: '', message: '', type: 'success' });

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        bio: '',
        profession: '',
        institution: '',
        country: '',
        specialties: [],
        researchAreas: [],
        articles: [],
        projects: []
    });

    const [newTag, setNewTag] = useState('');
    const [newSpecialty, setNewSpecialty] = useState('');

    const [articleCrud, setArticleCrud] = useState({
        isOpen: false,
        isEditing: false,
        index: null,
        temp: { title: '', journal: '', publicationDate: '', authors: [], description: '', link: '' }
    });
    const [projectCrud, setProjectCrud] = useState({
        isOpen: false,
        isEditing: false,
        index: null,
        temp: { title: '', startDate: '', endDate: '', description: '', details: '' }
    });

    useEffect(() => {
        // Solo refrescar si el usuario está cargado pero faltan datos críticos 
        // y no lo hemos intentado ya en este montaje del componente.
        const checkAndRefresh = async () => {
            const isInvestigator = user?.roles?.some(r => typeof r === 'string' ? r === 'INVESTIGATOR' : r.name === 'INVESTIGATOR');
            if (isInvestigator && !user.professionalProfile && !loading) {
                console.log('🔄 [ProfileEdit] Initial check: Professional profile missing, refreshing once...');
                setLoading(true);
                await refreshUser();
                setLoading(false);
            }
        };

        if (user && !avatarPreview && user.profile?.photoUrl) {
            // Cargar preview inicial si existe
            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const fullUrl = user.profile.photoUrl.startsWith('http')
                ? user.profile.photoUrl
                : `${backendUrl}${user.profile.photoUrl.startsWith('/') ? '' : '/'}${user.profile.photoUrl}`;
            setAvatarPreview(fullUrl);
        }

        // Evitamos el bucle infinito: solo ejecutamos esto una vez al montar o si el ID del usuario cambia
    }, [user?.id, refreshUser]);

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.profile?.firstName || '',
                lastName: user.profile?.lastName || '',
                bio: user.profile?.bio || '',
                profession: user.professionalProfile?.profession || '',
                institution: user.professionalProfile?.institution || '',
                country: user.profile?.country || '',
                specialties: Array.isArray(user.professionalProfile?.specialties)
                    ? user.professionalProfile.specialties
                    : [],
                researchAreas: Array.isArray(user.professionalProfile?.researchAreas)
                    ? user.professionalProfile.researchAreas
                    : [],
                articles: Array.isArray(user.profile?.articles)
                    ? user.profile.articles
                    : [],
                projects: Array.isArray(user.profile?.projects)
                    ? user.profile.projects
                    : []
            });
            if (user.profile?.photoUrl) {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
                const fullUrl = user.profile.photoUrl.startsWith('http')
                    ? user.profile.photoUrl
                    : `${backendUrl}${user.profile.photoUrl.startsWith('/') ? '' : '/'}${user.profile.photoUrl}`;
                setAvatarPreview(fullUrl);
            } else {
                setAvatarPreview(null);
            }
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAvatarClick = () => {
        avatarInputRef.current.click();
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setRemovePhoto(false);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveAvatar = (e) => {
        e.stopPropagation();
        setAvatarPreview(null);
        setSelectedFile(null);
        setRemovePhoto(true);
        if (avatarInputRef.current) avatarInputRef.current.value = '';
    };

    // --- Especialidades ---
    const handleAddSpecialty = () => {
        if (newSpecialty.trim() && !formData.specialties.includes(newSpecialty.trim())) {
            setFormData(prev => ({
                ...prev,
                specialties: [...prev.specialties, newSpecialty.trim()]
            }));
            setNewSpecialty('');
        }
    };
    const handleRemoveSpecialty = (item) => {
        setFormData(prev => ({ ...prev, specialties: prev.specialties.filter(s => s !== item) }));
    };

    // --- Líneas de Investigación ---
    const handleAddTag = () => {
        if (newTag.trim() && !formData.researchAreas.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                researchAreas: [...prev.researchAreas, newTag.trim()]
            }));
            setNewTag('');
        }
    };
    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            researchAreas: Array.isArray(prev.researchAreas) ? prev.researchAreas.filter(t => t !== tagToRemove) : []
        }));
    };

    // --- Artículos CRUD ---
    const handleOpenArticleForm = (index = null) => {
        if (index !== null) {
            setArticleCrud({
                isOpen: true,
                isEditing: true,
                index,
                temp: { ...formData.articles[index] }
            });
        } else {
            setArticleCrud({
                isOpen: true,
                isEditing: false,
                index: null,
                temp: { title: '', journal: '', publicationDate: '', authors: [], description: '', link: '' }
            });
        }
    };

    const handleCommitArticle = () => {
        const { isEditing, index, temp } = articleCrud;
        if (!temp.title.trim()) return;

        const updatedArticles = [...formData.articles];
        if (isEditing) {
            updatedArticles[index] = temp;
        } else {
            updatedArticles.push(temp);
        }

        setFormData(prev => ({ ...prev, articles: updatedArticles }));
        setArticleCrud({ ...articleCrud, isOpen: false });
    };

    const handleDeleteArticle = (index) => {
        if (window.confirm('¿Estás seguro de eliminar este artículo?')) {
            setFormData(prev => ({ ...prev, articles: prev.articles.filter((_, i) => i !== index) }));
        }
    };

    // --- Proyectos CRUD ---
    const handleOpenProjectForm = (index = null) => {
        if (index !== null) {
            setProjectCrud({
                isOpen: true,
                isEditing: true,
                index,
                temp: { ...formData.projects[index] }
            });
        } else {
            setProjectCrud({
                isOpen: true,
                isEditing: false,
                index: null,
                temp: { title: '', startDate: '', endDate: '', description: '', details: '' }
            });
        }
    };

    const handleCommitProject = () => {
        const { isEditing, index, temp } = projectCrud;
        if (!temp.title.trim()) return;

        const updatedProjects = [...formData.projects];
        if (isEditing) {
            updatedProjects[index] = temp;
        } else {
            updatedProjects.push(temp);
        }

        setFormData(prev => ({ ...prev, projects: updatedProjects }));
        setProjectCrud({ ...projectCrud, isOpen: false });
    };

    const handleDeleteProject = (index) => {
        if (window.confirm('¿Estás seguro de eliminar este proyecto?')) {
            setFormData(prev => ({ ...prev, projects: prev.projects.filter((_, i) => i !== index) }));
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const data = new FormData();
            data.append('firstName', formData.firstName);
            data.append('lastName', formData.lastName);
            data.append('bio', formData.bio);
            data.append('profession', formData.profession);
            data.append('institution', formData.institution);
            data.append('country', formData.country);
            // Enviar como strings compatibles con simple-array y el controller
            data.append('specialties', Array.isArray(formData.specialties) ? formData.specialties.join(',') : '');
            data.append('researchAreas', Array.isArray(formData.researchAreas) ? formData.researchAreas.join(',') : '');

            // Enviar complejos como JSON strings
            data.append('articles', JSON.stringify(Array.isArray(formData.articles) ? formData.articles : []));
            data.append('projects', JSON.stringify(Array.isArray(formData.projects) ? formData.projects : []));

            if (removePhoto) {
                data.append('removePhoto', 'true');
            } else if (selectedFile) {
                data.append('photo', selectedFile);
            }

            await userService.updateProfile(data, token);
            await refreshUser();
            setRemovePhoto(false);

            setFeedback({
                isOpen: true,
                title: 'Perfil Actualizado',
                message: 'Tu información ha sido guardada correctamente en la plataforma.',
                type: 'success'
            });
        } catch (err) {
            console.error('Error saving profile:', err);
            setFeedback({
                isOpen: true,
                title: 'Error',
                message: 'No se pudo actualizar el perfil. Intenta de nuevo más tarde.',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="pe-page">
            <Nav />
            <div className="pe-container">
                <header className="pe-header">
                    <div className="pe-header-text">
                        <h1>Configuración de Perfil</h1>
                        <p>Manten tu información profesional actualizada para la comunidad científica.</p>
                    </div>
                    <div className="pe-header-actions">
                        <button className="pe-btn-cancel" onClick={() => navigate('/feed')}>
                            <FaTimes /> Cancelar
                        </button>
                        <button className="pe-btn-save" onClick={handleSave} disabled={loading}>
                            {loading ? 'Guardando...' : <><FaSave /> Guardar Cambios</>}
                        </button>
                    </div>
                </header>

                <div className="pe-grid">
                    {/* SECCIÓN AVATAR */}
                    <div className="pe-section pe-avatar-section">
                        <h2 className="pe-section-title">Foto de Perfil</h2>
                        <div className="pe-avatar-wrapper">
                            <div className="pe-avatar-upload-container" onClick={handleAvatarClick}>
                                {avatarPreview ? (
                                    <img src={avatarPreview} alt="Preview" className="pe-avatar-preview" />
                                ) : (
                                    <div className="pe-avatar-placeholder">
                                        <span>{(formData.firstName?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase()}</span>
                                    </div>
                                )}
                                <div className="pe-avatar-overlay">
                                    <FaCamera />
                                    <span>Cambiar Foto</span>
                                </div>
                                <input
                                    type="file"
                                    ref={avatarInputRef}
                                    onChange={handleAvatarChange}
                                    style={{ display: 'none' }}
                                    accept="image/*"
                                />
                            </div>
                            {(avatarPreview || user?.profile?.photoUrl) && !removePhoto && (
                                <button className="pe-btn-remove-photo" onClick={handleRemoveAvatar} title="Eliminar foto">
                                    <FaTrash /> Eliminar Foto
                                </button>
                            )}
                        </div>
                    </div>

                    {/* INFORMACIÓN PERSONAL */}
                    <div className="pe-section">
                        <h2 className="pe-section-title">Información Personal</h2>
                        <div className="pe-form-row">
                            <div className="pe-form-group flex-1">
                                <label>Nombre</label>
                                <input
                                    type="text" name="firstName"
                                    value={formData.firstName} onChange={handleInputChange}
                                />
                            </div>
                            <div className="pe-form-group flex-1">
                                <label>Apellidos</label>
                                <input
                                    type="text" name="lastName"
                                    value={formData.lastName} onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className="pe-form-group">
                            <label>País / Nacionalidad</label>
                            <CountrySelector
                                value={formData.country}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="pe-form-group">
                            <label>Perfil General (Biografía)</label>
                            <textarea
                                name="bio" value={formData.bio}
                                onChange={handleInputChange}
                                placeholder="Cuéntanos sobre tu trayectoria y enfoque..."
                                rows={4}
                            />
                        </div>
                    </div>

                    {/* INFORMACIÓN PROFESIONAL */}
                    <div className="pe-section pe-full">
                        <h2 className="pe-section-title">Información Profesional</h2>

                        <div className="pe-professional-row">
                            <div className="pe-form-group">
                                <label><FaIdBadge /> Profesión / Cargo</label>
                                <input
                                    type="text" name="profession"
                                    value={formData.profession} onChange={handleInputChange}
                                    placeholder="Ej: Investigador Principal en Biotecnología..."
                                />
                            </div>
                            <div className="pe-form-group">
                                <label>Institución / Afiliación</label>
                                <input
                                    type="text" name="institution"
                                    value={formData.institution} onChange={handleInputChange}
                                    placeholder="Ej: Universidad Nacional, Centro de Investigación..."
                                />
                            </div>
                        </div>

                        <div className="pe-professional-row">
                            <div className="pe-form-group">
                                <label><FaStethoscope /> Especialidades</label>
                                <input
                                    type="text" value={newSpecialty}
                                    onChange={(e) => setNewSpecialty(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddSpecialty()}
                                    placeholder="Añade una especialidad..."
                                />
                            </div>
                            <button className="pe-btn-add-tag" onClick={handleAddSpecialty}>
                                <FaPlus /> añadir especialidad
                            </button>
                        </div>
                        <div className="pe-tags-container mb-4">
                            {Array.isArray(formData.specialties) && formData.specialties.map((item, i) => (
                                <span key={i} className="pe-tag-edit">
                                    {item} <FaTimes className="pe-remove-tag" onClick={() => handleRemoveSpecialty(item)} />
                                </span>
                            ))}
                        </div>

                        <div className="pe-professional-row">
                            <div className="pe-form-group">
                                <label><FaFlask /> linea de investigaciòn de interes</label>
                                <input
                                    type="text" value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                                    placeholder="Añade un tema..."
                                />
                            </div>
                            <button className="pe-btn-add-tag" onClick={handleAddTag}>
                                <FaPlus /> añadir linea de investigaciòn
                            </button>
                        </div>
                        <div className="pe-tags-container">
                            {Array.isArray(formData.researchAreas) && formData.researchAreas.map((tag, i) => (
                                <span key={i} className="pe-tag-edit">
                                    {tag} <FaTimes className="pe-remove-tag" onClick={() => handleRemoveTag(tag)} />
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* ARTÍCULOS CIENTÍFICOS */}
                    <div className="pe-section pe-full">
                        <div className="pe-section-header">
                            <h2 className="pe-section-title">Artículos Científicos Escritos</h2>
                            {!articleCrud.isOpen && (
                                <button className="pe-btn-add-item" onClick={() => handleOpenArticleForm()}>
                                    <FaPlus /> Añadir Artículo
                                </button>
                            )}
                        </div>

                        {articleCrud.isOpen ? (
                            <div className="pe-crud-form card-dark">
                                <h3 className="pe-crud-title">{articleCrud.isEditing ? 'Editar Artículo' : 'Nuevo Artículo'}</h3>
                                <div className="pe-form-row">
                                    <div className="pe-form-group flex-2">
                                        <label>Título del Artículo</label>
                                        <input
                                            type="text"
                                            value={articleCrud.temp.title}
                                            placeholder="Título..."
                                            onChange={(e) => setArticleCrud({ ...articleCrud, temp: { ...articleCrud.temp, title: e.target.value } })}
                                        />
                                    </div>
                                    <div className="pe-form-group flex-1">
                                        <label>Revista / Journal</label>
                                        <input
                                            type="text"
                                            value={articleCrud.temp.journal}
                                            placeholder="Revista..."
                                            onChange={(e) => setArticleCrud({ ...articleCrud, temp: { ...articleCrud.temp, journal: e.target.value } })}
                                        />
                                    </div>
                                </div>
                                <div className="pe-form-row">
                                    <div className="pe-form-group flex-1">
                                        <label>Fecha de Publicación</label>
                                        <input
                                            type="date"
                                            value={articleCrud.temp.publicationDate}
                                            onChange={(e) => setArticleCrud({ ...articleCrud, temp: { ...articleCrud.temp, publicationDate: e.target.value } })}
                                        />
                                    </div>
                                    <div className="pe-form-group flex-2">
                                        <label>Autores (separados por coma)</label>
                                        <input
                                            type="text"
                                            value={articleCrud.temp.authors ? articleCrud.temp.authors.join(', ') : ''}
                                            placeholder="Autores..."
                                            onChange={(e) => setArticleCrud({ ...articleCrud, temp: { ...articleCrud.temp, authors: e.target.value.split(',').map(s => s.trim()) } })}
                                        />
                                    </div>
                                </div>
                                <div className="pe-form-group">
                                    <label>Descripción / Abstract</label>
                                    <textarea
                                        value={articleCrud.temp.description}
                                        rows={3}
                                        placeholder="Breve resumen..."
                                        onChange={(e) => setArticleCrud({ ...articleCrud, temp: { ...articleCrud.temp, description: e.target.value } })}
                                    />
                                </div>
                                <div className="pe-form-group">
                                    <label>Enlace (DOI, PubMed)</label>
                                    <input
                                        type="text"
                                        value={articleCrud.temp.link}
                                        placeholder="https://..."
                                        onChange={(e) => setArticleCrud({ ...articleCrud, temp: { ...articleCrud.temp, link: e.target.value } })}
                                    />
                                </div>
                                <div className="pe-crud-actions">
                                    <button className="pe-btn-cancel-sm" onClick={() => setArticleCrud({ ...articleCrud, isOpen: false })}>
                                        Cancelar
                                    </button>
                                    <button className="pe-btn-confirm-sm" onClick={handleCommitArticle}>
                                        <FaCheck /> {articleCrud.isEditing ? 'Actualizar' : 'Añadir'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="pe-table-container">
                                {formData.articles.length > 0 ? (
                                    <table className="pe-table">
                                        <thead>
                                            <tr>
                                                <th>Título</th>
                                                <th>Revista</th>
                                                <th>Fecha</th>
                                                <th className="text-center">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(formData.articles) && formData.articles.map((art, idx) => (
                                                <tr key={idx}>
                                                    <td>{art.title}</td>
                                                    <td>{art.journal}</td>
                                                    <td>{art.publicationDate}</td>
                                                    <td className="pe-actions-cell">
                                                        <button className="pe-action-btn edit" title="Editar" onClick={() => handleOpenArticleForm(idx)}>
                                                            <FaEdit />
                                                        </button>
                                                        <button className="pe-action-btn delete" title="Eliminar" onClick={() => handleDeleteArticle(idx)}>
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="pe-empty-text">No has registrado artículos aún.</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* PROYECTOS DE INVESTIGACIÓN */}
                    <div className="pe-section pe-full">
                        <div className="pe-section-header">
                            <h2 className="pe-section-title">Proyectos de Investigación</h2>
                            {!projectCrud.isOpen && (
                                <button className="pe-btn-add-item" onClick={() => handleOpenProjectForm()}>
                                    <FaPlus /> Añadir Proyecto
                                </button>
                            )}
                        </div>

                        {projectCrud.isOpen ? (
                            <div className="pe-crud-form card-dark">
                                <h3 className="pe-crud-title">{projectCrud.isEditing ? 'Editar Proyecto' : 'Nuevo Proyecto'}</h3>
                                <div className="pe-form-group">
                                    <label>Nombre del Proyecto</label>
                                    <input
                                        type="text"
                                        value={projectCrud.temp.title}
                                        placeholder="Nombre..."
                                        onChange={(e) => setProjectCrud({ ...projectCrud, temp: { ...projectCrud.temp, title: e.target.value } })}
                                    />
                                </div>
                                <div className="pe-form-row">
                                    <div className="pe-form-group flex-1">
                                        <label>Fecha Inicio</label>
                                        <input
                                            type="date"
                                            value={projectCrud.temp.startDate}
                                            onChange={(e) => setProjectCrud({ ...projectCrud, temp: { ...projectCrud.temp, startDate: e.target.value } })}
                                        />
                                    </div>
                                    <div className="pe-form-group flex-1">
                                        <label>Fecha Fin</label>
                                        <input
                                            type="date"
                                            value={projectCrud.temp.endDate}
                                            onChange={(e) => setProjectCrud({ ...projectCrud, temp: { ...projectCrud.temp, endDate: e.target.value } })}
                                        />
                                    </div>
                                </div>
                                <div className="pe-form-group">
                                    <label>Descripción</label>
                                    <textarea
                                        value={projectCrud.temp.description}
                                        rows={3}
                                        placeholder="Resumen del proyecto..."
                                        onChange={(e) => setProjectCrud({ ...projectCrud, temp: { ...projectCrud.temp, description: e.target.value } })}
                                    />
                                </div>
                                <div className="pe-form-group">
                                    <label>Tu participación / Detalles</label>
                                    <textarea
                                        value={projectCrud.temp.details}
                                        rows={2}
                                        placeholder="Rol, objetivos logrados..."
                                        onChange={(e) => setProjectCrud({ ...projectCrud, temp: { ...projectCrud.temp, details: e.target.value } })}
                                    />
                                </div>
                                <div className="pe-crud-actions">
                                    <button className="pe-btn-cancel-sm" onClick={() => setProjectCrud({ ...projectCrud, isOpen: false })}>
                                        Cancelar
                                    </button>
                                    <button className="pe-btn-confirm-sm" onClick={handleCommitProject}>
                                        <FaCheck /> {projectCrud.isEditing ? 'Actualizar' : 'Añadir'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="pe-table-container">
                                {formData.projects.length > 0 ? (
                                    <table className="pe-table">
                                        <thead>
                                            <tr>
                                                <th>Nombre del Proyecto</th>
                                                <th>Inicio</th>
                                                <th>Fin</th>
                                                <th className="text-center">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(formData.projects) && formData.projects.map((proj, idx) => (
                                                <tr key={idx}>
                                                    <td>{proj.title}</td>
                                                    <td>{proj.startDate}</td>
                                                    <td>{proj.endDate}</td>
                                                    <td className="pe-actions-cell">
                                                        <button className="pe-action-btn edit" title="Editar" onClick={() => handleOpenProjectForm(idx)}>
                                                            <FaEdit />
                                                        </button>
                                                        <button className="pe-action-btn delete" title="Eliminar" onClick={() => handleDeleteProject(idx)}>
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="pe-empty-text">No has registrado proyectos aún.</p>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            </div>
            <FeedbackModal
                isOpen={feedback.isOpen}
                title={feedback.title}
                message={feedback.message}
                type={feedback.type}
                onClose={() => {
                    setFeedback({ ...feedback, isOpen: false });
                    if (feedback.type === 'success') {
                        navigate('/feed');
                    }
                }}
            />
        </div>
    );
};

export default ProfileEdit;
